import React, { createContext, useCallback, useState } from 'react'

import { isArray, isObject } from 'lodash'
import { connect } from 'react-redux'
import { Modal } from 'antd'

import { selectUser } from '../../../reducers/UserReducer'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import useInternship from '../../../utils/api/hooks/useInternship'
import { Actions } from '../../../utils/events'
import SingleInternshipForm from './SingleInternshipForm'
import BulkInternshipForm from './BulkInternshipForm'
import { InternshipValidators } from '../../../utils/entities/internship'
import { selectValidators } from '../../../utils/validators'
import { px, vw } from '../../../utils/cssHelper'

export const InternshipFormModalContext = createContext()

export const Types = Object.freeze({
  SINGLE: 'single',
  BULK: 'bulk'
})

const DEFAULT_FORM_HANDLER = { data: null, callback: null, type: Types.SINGLE }

const mapStateToProps = state => ({ user: selectUser(state), t: selectTranslate(state) })

const InternshipFormModal = ({ keysToValidate = [], children, user, sectors, t }) => {
  const [formHandler, setFormHandler] = useState(DEFAULT_FORM_HANDLER)
  const [loading, setLoading] = useState(false)

  const internshipsHandler = useInternship({ user, t, callback: formHandler.callback })

  const handleSubmit = useCallback(data => {
    setLoading(true)

    if (isArray(data)) {
      internshipsHandler.create(data).finally(handleCloseModal)
    } else {
      internshipsHandler.upsert(data, formHandler.data, true).finally(handleCloseModal)
    }
  }, [internshipsHandler.create, internshipsHandler.upsert, formHandler.data])

  const handleDelete = useCallback(() => {
    setLoading(true)

    internshipsHandler.delete(formHandler.data).finally(handleCloseModal)
  }, [internshipsHandler.delete, formHandler.data])

  const handleCloseModal = useCallback(() => setFormHandler(DEFAULT_FORM_HANDLER), [])

  const action = formHandler.data?.id === -1 ? Actions.CREATE.toLocaleLowerCase() : Actions.UPDATE.toLocaleLowerCase()

  const validators = selectValidators(InternshipValidators, keysToValidate)

  return (
    <>
      <InternshipFormModalContext.Provider value={{ setFormHandler }}>
        {children}
      </InternshipFormModalContext.Provider>
      <Modal
        className='form-modal'
        width={vw(80)}
        style={{ minWidth: px(525), maxWidth: formHandler.type === Types.BULK ? px(800) : px(600) }}
        title={t(`internship_form_modal.title.${formHandler.type.toLowerCase()}.${action}`)}
        visible={isObject(formHandler.data)}
        onCancel={handleCloseModal}
        afterClose={() => setLoading(false)}
        destroyOnClose
        footer={null}
      >
        {formHandler.type === Types.SINGLE && isObject(formHandler.data) && (
          <SingleInternshipForm
            data={formHandler.data}
            sectors={sectors}
            disabled={loading}
            validators={validators}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            onCancel={handleCloseModal}
          />
        )}
        {formHandler.type === Types.BULK && isObject(formHandler.data) && (
          <BulkInternshipForm
            data={formHandler.data}
            quantity={formHandler.quantity}
            sectors={sectors}
            disabled={loading}
            validators={validators}
            onCancel={handleCloseModal}
            onDelete={handleDelete}
            onSubmit={handleSubmit}
          />
        )}
      </Modal>
    </>
  )
}

export default connect(mapStateToProps)(InternshipFormModal)
